import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../context/ConfirmContext';
import { taskCommentsAPI } from '../../services/taskServices';
import { TaskComment } from '../../types/task';

interface TaskCommentsProps {
  taskId: number;
}

const TaskComments: React.FC<TaskCommentsProps> = ({ taskId }) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [taskId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await taskCommentsAPI.getAll(taskId);
      setComments(response.data.comments);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await taskCommentsAPI.create(taskId, { body: newComment });
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: number) => {
    if (!replyText.trim() || submitting) return;

    setSubmitting(true);
    try {
      await taskCommentsAPI.create(taskId, { body: replyText, parent_comment_id: parentId });
      setReplyTo(null);
      setReplyText('');
      fetchComments();
    } catch (err) {
      console.error('Failed to add reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId: number) => {
    if (!editText.trim() || submitting) return;

    setSubmitting(true);
    try {
      await taskCommentsAPI.update(taskId, commentId, editText);
      setEditingId(null);
      setEditText('');
      fetchComments();
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    const confirmed = await confirm({
      title: t('tasks.deleteCommentTitle'),
      message: t('tasks.deleteCommentConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await taskCommentsAPI.delete(taskId, commentId);
      fetchComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.justNow');
    if (diffMins < 60) return t('common.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const renderComment = (comment: TaskComment, isReply = false) => (
    <div key={comment.id} className={`${isReply ? 'ml-8 mt-3' : 'border-b border-gray-100 pb-4'}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
          {comment.user.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm">{comment.user.name}</span>
            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
            {comment.edited && (
              <span className="text-xs text-gray-400 italic">({t('common.edited')})</span>
            )}
          </div>

          {editingId === comment.id ? (
            <div className="mt-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => handleEdit(comment.id)}
                  disabled={submitting}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="px-3 py-1 text-gray-600 text-sm hover:text-gray-800"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{comment.body}</p>

              <div className="mt-2 flex items-center gap-3">
                {!isReply && (
                  <button
                    onClick={() => {
                      setReplyTo(replyTo === comment.id ? null : comment.id);
                      setReplyText('');
                    }}
                    className="text-xs text-gray-500 hover:text-blue-600"
                  >
                    {t('tasks.reply')}
                  </button>
                )}
                {comment.can_edit && (
                  <>
                    <button
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditText(comment.body);
                      }}
                      className="text-xs text-gray-500 hover:text-blue-600"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="text-xs text-gray-500 hover:text-red-600"
                    >
                      {t('common.delete')}
                    </button>
                  </>
                )}
              </div>
            </>
          )}

          {/* Reply form */}
          {replyTo === comment.id && (
            <div className="mt-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={t('tasks.writeReply')}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => handleReply(comment.id)}
                  disabled={submitting || !replyText.trim()}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('tasks.reply')}
                </button>
                <button
                  onClick={() => setReplyTo(null)}
                  className="px-3 py-1 text-gray-600 text-sm hover:text-gray-800"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-3 space-y-3">
              {comment.replies.map((reply) => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-16 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New comment form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t('tasks.writeComment')}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows={3}
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? t('common.saving') : t('tasks.addComment')}
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-center text-gray-500 py-8">{t('tasks.noComments')}</p>
        ) : (
          comments.map((comment) => renderComment(comment))
        )}
      </div>
    </div>
  );
};

export default TaskComments;
